import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";
import { z } from "zod";

// Pure identity resolution shared by every package in the workspace. Runtime
// modules (serviceIdentity.ts, the standalone services' env.ts) read their own
// variables and call getServiceIdentityFromInput.
// The Fly proxy and the Cloud Run video services keep a copy of the parser,
// because their images are built from the service directory alone.

export const ACTIVE_DEPLOYMENT_REGIONS = ["us", "eu"] as const;
export type ActiveDeploymentRegion = (typeof ACTIVE_DEPLOYMENT_REGIONS)[number];

// Includes "missing" as a temporary local/unset sentinel. Drop it from the
// enum once every stack injects a real region at boot.
export const DEPLOYMENT_REGIONS = [
  ...ACTIVE_DEPLOYMENT_REGIONS,
  "missing",
] as const;
export const deploymentRegionSchema = z.enum(DEPLOYMENT_REGIONS);
export type DeploymentRegion = z.infer<typeof deploymentRegionSchema>;

export const SERVICE_ENVIRONMENTS = [
  "production",
  "staging",
  "preview",
  "development",
  "test",
] as const;
export const serviceEnvironmentSchema = z.enum(SERVICE_ENVIRONMENTS);
export type ServiceEnvironment = z.infer<typeof serviceEnvironmentSchema>;

export {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";
/** Not in semconv: `cloud.region` is the provider region, this is the US/EU stack. */
export const ATTR_DEPLOYMENT_REGION = "deployment.region";

export type ServiceIdentity = Readonly<{
  service: string;
  environment: ServiceEnvironment;
  deploymentRegion: DeploymentRegion;
  version: string | undefined;
}>;

export type ResourceAttributes = Readonly<{
  [ATTR_SERVICE_NAME]: string;
  [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: ServiceEnvironment;
  [ATTR_DEPLOYMENT_REGION]: DeploymentRegion;
  [ATTR_SERVICE_VERSION]?: string;
}>;

/** `OTEL_RESOURCE_ATTRIBUTES` is `key=value,key=value`, values percent-encoded. */
export const parseResourceAttributes = (
  value: string | undefined
): Record<string, string> => {
  const entries: [string, string][] = (value ?? "")
    .split(",")
    .map((pair) => pair.trim())
    .filter((pair) => pair.includes("="))
    .map((pair): [string, string] => {
      const separatorIndex = pair.indexOf("=");
      const key = pair.slice(0, separatorIndex).trim();
      const raw = pair.slice(separatorIndex + 1).trim();
      try {
        return [key, decodeURIComponent(raw)];
      } catch {
        return [key, raw];
      }
    })
    .filter(([key, attributeValue]) => key && attributeValue);
  return Object.fromEntries(entries);
};

export const parseServiceEnvironment = (
  value: string | undefined
): ServiceEnvironment | undefined => {
  const parsed = serviceEnvironmentSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

export type ServiceIdentityInput = Readonly<{
  /** Name when OTEL_SERVICE_NAME is unset (the web server and the browser have no per-process config). */
  defaultServiceName: string;
  otelServiceName?: string;
  otelResourceAttributes?: string;
  /** DEPLOYMENT_REGION, already validated by the runtime's env module. Authoritative; telemetry cannot override it. */
  deploymentRegion?: string;
  vercelEnv?: string;
  jobsEnv?: string;
  nodeEnv?: string;
  renderExternalUrl?: string;
  commitSha?: string;
}>;

export type ServiceIdentityWarning = Readonly<{
  attribute: string;
  value: string;
  reason: "invalid" | "ignored";
}>;

const nonEmpty = (value: string | undefined): string | undefined =>
  value ? value : undefined;

const isOnrenderExternalUrl = (value: string | undefined): boolean => {
  if (!value || !URL.canParse(value)) {
    return false;
  }
  const hostname = new URL(value).hostname;
  return hostname === "onrender.com" || hostname.endsWith(".onrender.com");
};

const getEnvironment = (
  input: ServiceIdentityInput,
  configured: Record<string, string>,
  warnings: ServiceIdentityWarning[]
): ServiceEnvironment => {
  // A project-wide value on Vercel would otherwise label previews as production.
  if (input.vercelEnv === "preview") {
    return "preview";
  }
  const configuredValue =
    configured[ATTR_DEPLOYMENT_ENVIRONMENT_NAME] ??
    // Pre-1.27 semconv key, still what dd-trace documents.
    configured["deployment.environment"];
  const configuredEnvironment = parseServiceEnvironment(configuredValue);
  if (configuredEnvironment) {
    return configuredEnvironment;
  }
  if (configuredValue) {
    warnings.push({
      attribute: ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
      value: configuredValue,
      reason: "invalid",
    });
  }
  if (input.vercelEnv === "production" || input.vercelEnv === "development") {
    return input.vercelEnv;
  }
  const jobsEnv = parseServiceEnvironment(input.jobsEnv);
  if (jobsEnv) {
    return jobsEnv;
  }
  if (input.nodeEnv === "test") {
    return "test";
  }
  if (input.nodeEnv === "production") {
    return "production";
  }
  if (isOnrenderExternalUrl(input.renderExternalUrl)) {
    return "preview";
  }
  return "development";
};

/**
 * Nothing here throws on a bad telemetry value and nothing here reads the
 * process: a worker labeled `development` is still a running worker, and
 * callers decide where the warnings go (after they have cached the result,
 * so a logger that itself asks for the identity cannot recurse).
 */
export const getServiceIdentityFromInput = (
  input: ServiceIdentityInput
): { identity: ServiceIdentity; warnings: ServiceIdentityWarning[] } => {
  const warnings: ServiceIdentityWarning[] = [];
  const configured = parseResourceAttributes(input.otelResourceAttributes);

  const service =
    nonEmpty(input.otelServiceName) ??
    nonEmpty(configured[ATTR_SERVICE_NAME]) ??
    input.defaultServiceName;

  const deploymentRegion = deploymentRegionSchema.parse(
    input.deploymentRegion || "missing"
  );
  const configuredRegion = configured[ATTR_DEPLOYMENT_REGION];
  if (configuredRegion && configuredRegion !== deploymentRegion) {
    warnings.push({
      attribute: ATTR_DEPLOYMENT_REGION,
      value: configuredRegion,
      reason: "ignored",
    });
  }

  return {
    identity: Object.freeze({
      service,
      environment: getEnvironment(input, configured, warnings),
      deploymentRegion,
      version:
        nonEmpty(configured[ATTR_SERVICE_VERSION]) ?? nonEmpty(input.commitSha),
    }),
    warnings,
  };
};

/** The canonical representation. Vendor layers convert from this. */
export const toResourceAttributes = ({
  service,
  environment,
  deploymentRegion,
  version,
}: ServiceIdentity): ResourceAttributes => ({
  [ATTR_SERVICE_NAME]: service,
  [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: environment,
  [ATTR_DEPLOYMENT_REGION]: deploymentRegion,
  ...(version ? { [ATTR_SERVICE_VERSION]: version } : {}),
});
